import { useMemo, useState } from 'react';
import { useLocation, getLocale } from 'umi';
import { useMemoizedFn, useAsyncEffect } from 'ahooks';
import { fetchPDFFileStream, fetchPDFFileStreamSearch } from '@intelligence/services/common';
import {
  getReportByIdApi,
  getReportBySystemTypeApi,
  getReportPermission,
  getStudioPermission,
} from '@intelligence/services/reportService';
import { getPDFPreviewDownloadUrlParams } from '@intelligence/pages/PDFPreviewDownload/utils';
import {
  pageReportTypeConfig,
  findReportObjectBySystemId,
} from '@intelligence/_config/reportConfig';
import { getReportOriginalResourcePath, getReportResourcePath } from '@intelligence/utils/reports';
import { getIsHTMLReportPath, getIsZipReportPath } from '@home/utils/reportPreview';
import { getStudioReportList } from '@home/services/competitor';

import type { IPDFPreviewDownloadURLSearchParams } from '@intelligence/pages/PDFPreviewDownload/types';

const newApiReport = [
  pageReportTypeConfig.gameResearchReport.systemIdCur,
  pageReportTypeConfig.insightsReports.systemIdCur,
  pageReportTypeConfig.studioReport.systemIdCur,
];

export default function usePDFPreviewDownload() {
  const { search } = useLocation();

  const [downloadable, setDownloadable] = useState(false); // 用户是否有报告下载权限
  const [studioAble, setStudioAble] = useState(false); // 用户是否有对应studio的权限
  const [studioAbleLoading, setStudioAbleLoading] = useState(true); // studio的权限接口loading
  const [iicLoading, setIicLoading] = useState(false); // iic 报告权限接口 loading
  const urlParams =
    useMemo<IPDFPreviewDownloadURLSearchParams>(() => {
      return getPDFPreviewDownloadUrlParams(search) as IPDFPreviewDownloadURLSearchParams;
    }, [search]);

  const { systemId, resourceId, lang, studioId, fileName: fileNameFromUrl } = urlParams;

  // 新 URL 格式：不含 resourceName，需要通过 API 获取缺失字段
  const isShortUrl = !urlParams.resourceName;
  const isStudioReport = systemId === pageReportTypeConfig.studioReport.systemIdCur;
  // 仅专区且 fileName 能认出 html/zip 时提前预览；PDF / 未知后缀等详情，避免空 path 被当成 PDF
  const canStudioPreviewEarly =
    isStudioReport
    && (getIsHTMLReportPath(fileNameFromUrl) || getIsZipReportPath(fileNameFromUrl));

  const [detailLoading, setDetailLoading] = useState(
    isShortUrl && !canStudioPreviewEarly,
  );

  const [apiReportDetail, setApiReportDetail] = useState<{
    id: string;
    resourceName: string;
    resourcePath: string;
    resourceOriginalPath: string;
    resourceLike: string;
    downloadLogKey: string;
    watermarkEnable: string;
  } | null>(null);

  // 旧格式：直接取 URL 参数；新格式：取 API 返回的数据
  // 专区短链会带管理端 id（浏览量接口依赖）；情报仍走详情接口补 id
  const id = isStudioReport
    ? (urlParams.id || (isShortUrl ? (apiReportDetail?.id ?? '') : ''))
    : (isShortUrl ? (apiReportDetail?.id ?? '') : (urlParams.id ?? ''));
  const resourceName = isShortUrl ? (apiReportDetail?.resourceName ?? '') : (urlParams.resourceName ?? '');
  // fileName 只用于专区判断 html/zip，不能当作 COS key
  const fileExtName = isStudioReport ? (fileNameFromUrl || '') : '';
  const resourcePath = urlParams.resourcePath
    || (isShortUrl ? (apiReportDetail?.resourcePath ?? '') : '');
  const resourceOriginalPath = isShortUrl
    ? apiReportDetail?.resourceOriginalPath ?? ''
    : urlParams.resourceOriginalPath ?? '';
  const resourceLike = isShortUrl ? (apiReportDetail?.resourceLike ?? '') : (urlParams.resourceLike ?? '');
  const downloadLogKey = isShortUrl ? (apiReportDetail?.downloadLogKey ?? '') : (urlParams.downloadLogKey ?? '');
  const watermarkEnable = isShortUrl ? (apiReportDetail?.watermarkEnable ?? '') : (urlParams.watermarkEnable ?? '');

  // 新格式 URL：调 API 获取缺失的报告详情字段
  useAsyncEffect(async () => {
    if (!isShortUrl || !resourceId) return;

    try {
      const res = await getReportByIdApi({ resource_ids: [resourceId] });
      const report = res?.resource_list?.[0];

      if (!report) {
        console.error('Report not found for resourceId:', resourceId);
        return;
      }

      const locale = getLocale();
      const reportCfg = findReportObjectBySystemId(
        pageReportTypeConfig,
        systemId,
      );

      setApiReportDetail({
        id: String(report.id ?? ''),
        resourceName: (lang === 'en'
          ? report.title_en || report.title
          : report.title || report.title_en) || '',
        resourcePath: getReportResourcePath(locale, {
          lang: lang || undefined,
          pdf: report.pdf,
          pdf_en: report.pdf_en,
        }),
        resourceOriginalPath: getReportOriginalResourcePath(locale, {
          lang: lang || undefined,
          original_file_name: report.original_file_name,
        }),
        resourceLike: String(report.report_like ?? 0),
        downloadLogKey: typeof reportCfg?.downloadLogKeyCur === 'string'
          ? reportCfg.downloadLogKeyCur
          : '',
        watermarkEnable: report?.wartermark_enable || '',
      });
    } catch (error) {
      console.error('Failed to fetch report detail:', error);
    } finally {
      setDetailLoading(false);
    }
  }, [isShortUrl, resourceId, systemId]);

  const fetchPDFUrl = useMemoizedFn(async () =>
    newApiReport.includes(systemId)
      ? fetchPDFFileStreamSearch({ systemId, resourceId, lang })
      : fetchPDFFileStream({ systemId, resourceId, lang }),
  );

  // 获取到报告结果，设置报告下载权限
  const fetchSetReportAble = (res: any) => {
    /**
     * 产品要求 配了不支持下载 才不能下载，其他情况默认可以下载
     * 0 代表不能下载
     */
    const resource = res?.resource_list?.find((item: any) => item?.id === id && item?.resource_id === resourceId);
    const downloadableTmp = resource?.download_enable !== '0';
    setDownloadable(downloadableTmp);
  };

  const fetchStudioReportList = async () => {
    try {
      const res = await getStudioReportList({ id });
      fetchSetReportAble(res);
    } catch (exception) {
      setDownloadable(true);
    }
  };

  // 情报短链等详情接口；专区已带管理端 id 时可先走权限
  const permissionReady =
    !isShortUrl || !!apiReportDetail || (isStudioReport && !!urlParams.id);

  useAsyncEffect(async () => {
    if (!permissionReady) return;

    try {
      // 如果报告是studio的，先判断用户是否有studio权限，再获取studio报告数据
      if (systemId === pageReportTypeConfig.studioReport.systemIdCur && studioId) {
        const resPer = await getStudioPermission({ studio_code: studioId });
        // 有studio权限
        if (resPer?.code === 0) {
          fetchStudioReportList();
          setStudioAble(true);
        }
        setStudioAbleLoading(false);
      } else if (systemId === pageReportTypeConfig.insightsCenterLibrary.systemIdCur) {
        // iic 报告 download_enable 下载权限
        setIicLoading(true);
        const res = await getReportPermission({ id });
        setDownloadable(res?.data?.download_enable !== '0');
        setIicLoading(false);
        setStudioAbleLoading(false);
      } else {
        // 其他报告不需要判断权限
        const res = await getReportBySystemTypeApi(systemId);
        fetchSetReportAble(res);
        setStudioAbleLoading(false);
      }
    } catch (exception) {
      setDownloadable(true);
      setIicLoading(false);
      setStudioAbleLoading(false);
    }
  }, [permissionReady]);

  return {
    id,
    systemId,
    resourceId,
    resourceName,
    resourcePath,
    resourceOriginalPath,
    resourceLike,
    downloadable,
    downloadLogKey,
    watermarkEnable,
    fetchPDFUrl,
    lang,
    studioAble,
    studioId,
    studioAbleLoading,
    iicLoading,
    detailLoading,
    fileExtName,
  };
}
